import {
  createContext,
  useContext,
  useCallback,
  useState,
  ReactNode,
} from "react";

interface SelectionContextType {
  selectedIds: Set<string>;
  isSelected: (id: string) => boolean;
  toggleSelection: (id: string) => void;
  selectAll: (ids: string[]) => void;
  clearSelection: () => void;
  selectionCount: number;
}

const SelectionContext = createContext<SelectionContextType | undefined>(
  undefined,
);

export function SelectionProvider({
  children,
  initialSelection = new Set<string>(),
  onSelectionChange,
}: {
  children: ReactNode;
  initialSelection?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(initialSelection);

  const toggleSelection = useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        onSelectionChange?.(newSet);
        return newSet;
      });
    },
    [onSelectionChange],
  );

  const selectAll = useCallback(
    (ids: string[]) => {
      setSelectedIds((prev) => {
        if (ids.length === prev.size && ids.every((id) => prev.has(id))) {
          onSelectionChange?.(new Set());
          return new Set();
        }
        const newSet = new Set(ids);
        onSelectionChange?.(newSet);
        return newSet;
      });
    },
    [onSelectionChange],
  );

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    onSelectionChange?.(new Set());
  }, [onSelectionChange]);

  const isSelected = useCallback(
    (id: string) => {
      return selectedIds.has(id);
    },
    [selectedIds],
  );

  const value = {
    selectedIds,
    isSelected,
    toggleSelection,
    selectAll,
    clearSelection,
    selectionCount: selectedIds.size,
  };

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
}

export function useSelection() {
  const context = useContext(SelectionContext);
  if (context === undefined) {
    throw new Error("useSelection must be used within a SelectionProvider");
  }
  return context;
}
